import React from "react";
import { Button, IconButton, Typography } from "@mui/material";
import EditIcon from "@material-ui/icons/Edit";

import { dateFormatter } from "components/helpers";
import { getFormattedDate, trimmedDate } from "utils/helper";
import dayjs from "dayjs";

const valueFormatter = (value) => (value ? getFormattedDate(value) : "");

const valueGetter = (value) => dateFormatter(value);

const hypertensionColumns = (handleOnEditClick, handleAssignUser) => [
  {
    field: "edit",
    headerName: "",
    width: 100,
    align: "center",
    renderCell: (e) => (
      <>
        <IconButton onClick={() => handleOnEditClick(e.row)} color="secondary">
          <EditIcon fontSize="small" />
        </IconButton>
      </>
    ),
  },
  {
    field: "patient_id",
    headerName: "ID",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "care_oppt_category",
    headerName: "Care Opportunity Category",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "ext_source_name",
    headerName: "External Source Name",
    minWidth: 180,
    flex: 1,
  },
  { field: "status", headerName: "Status", minWidth: 150, flex: 1 },
  {
    field: "outreach_status",
    headerName: "Outreach Status",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "created_date",
    headerName: "Created Date",
    minWidth: 150,
    flex: 1,
    valueFormatter: valueFormatter,
    valueGetter: valueGetter,
  },
  {
    field: "assigned_user_name",
    headerName: "Assigned User",
    minWidth: 180,
    flex: 1,
    renderCell: (e) => (
      <>
        {e.value ? (
          <Typography>{e.value}</Typography>
        ) : (
          <Button
            onClick={() => handleAssignUser(e.row)}
            variant="text"
            color="secondary"
          >
            Assign To me
          </Button>
        )}
      </>
    ),
  },
  { field: "patient_id", headerName: "ID", minWidth: 100, flex: 1 },
  { field: "patient_name", headerName: "Name", minWidth: 100, flex: 1 },
  {
    field: "patient_dob",
    headerName: "DOB",
    minWidth: 100,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  { field: "patient_age", headerName: "Age", minWidth: 100, flex: 1 },
  { field: "patient_gender", headerName: "Sex", minWidth: 100, flex: 1 },
  { field: "patient_phone", headerName: "Phone", minWidth: 150, flex: 1 },
  { field: "provider_name", headerName: "Provider", minWidth: 180, flex: 1 },
  {
    field: "last_obs_bp",
    headerName: "Last Observed BP",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "last_obs_bp_date",
    headerName: "Last Observed BP Date",
    minWidth: 180,
    flex: 1,
    valueFormatter: valueFormatter,
    valueGetter: valueGetter,
  },
  {
    field: "bp_category",
    headerName: "HTN Category/Diagnosis",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "action_rcmd",
    headerName: "Recommended Action",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "next_appt_type",
    headerName: "Next Appt Type",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "next_appt_date",
    headerName: "Next Appt Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "next_appt_time",
    headerName: "Next Appt Time",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <div>{e.value ? dayjs(e.value).format("hh:mm a") : "N/A"}</div>
    ),
  },
];

const wellnessColumns = (handleOnEditClick, handleAssignUser) => [
  {
    field: "edit",
    headerName: "",
    width: 100,
    align: "center",
    renderCell: (e) => (
      <>
        <IconButton onClick={() => handleOnEditClick(e.row)} color="secondary">
          <EditIcon fontSize="small" />
        </IconButton>
      </>
    ),
  },
  {
    field: "patient_id",
    headerName: "ID",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "care_oppt_category",
    headerName: "Care Opportunity Category",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "ext_source_name",
    headerName: "External Source Name",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "outreach_status",
    headerName: "Outreach Status",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "created_date",
    headerName: "Created Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "assigned_user_name",
    headerName: "Assigned User",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <>
        {e.value ? (
          <Typography>{e.value}</Typography>
        ) : (
          <Button
            onClick={() => handleAssignUser(e.row)}
            variant="text"
            color="secondary"
          >
            Assign To me
          </Button>
        )}
      </>
    ),
  },
  {
    field: "patient_name",
    headerName: "Name",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "patient_dob",
    headerName: "DOB",
    minWidth: 100,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "patient_phone",
    headerName: "Phone",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "location",
    headerName: "Location",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "ext_outreach_status",
    headerName: "External Outreach Status",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "ext_outreach_date",
    headerName: "Outreach Date",
    minWidth: 180,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "payer_name",
    headerName: "Payer",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "awv_due_date",
    headerName: "AWV Due On/After",
    minWidth: 180,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "last_awv_provider",
    headerName: "Last AWV Provider",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "returned_for",
    headerName: "Returned For",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "attribution_status",
    headerName: "Attribution Status",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "attribution_risk",
    headerName: "Attribution Risk",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "due_for",
    headerName: "Due For",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "next_appt_type",
    headerName: "Next Appt Type",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "next_appt_date",
    headerName: "Next Appt Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "next_appt_time",
    headerName: "Next Appt Time",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <div>{e.value ? dayjs(e.value).format("hh:mm a") : "N/A"}</div>
    ),
  },
];

const transitionOfCareColumns = (handleOnEditClick, handleAssignUser) => [
  {
    field: "edit",
    headerName: "",
    width: 100,
    align: "center",
    renderCell: (e) => (
      <>
        <IconButton onClick={() => handleOnEditClick(e.row)} color="secondary">
          <EditIcon fontSize="small" />
        </IconButton>
      </>
    ),
  },
  {
    field: "patient_id",
    headerName: "ID",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "care_oppt_category",
    headerName: "Care Opportunity Category",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "ext_source_name",
    headerName: "External Source Name",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "outreach_status",
    headerName: "Outreach Status",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "created_date",
    headerName: "Created Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "assigned_user_name",
    headerName: "Assigned User",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <>
        {e.value ? (
          <Typography>{e.value}</Typography>
        ) : (
          <Button
            onClick={() => handleAssignUser(e.row)}
            variant="text"
            color="secondary"
          >
            Assign To me
          </Button>
        )}
      </>
    ),
  },
  {
    field: "patient_name",
    headerName: "Name",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "patient_dob",
    headerName: "DOB",
    minWidth: 100,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "patient_phone",
    headerName: "Phone",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "location",
    headerName: "Location",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "ext_outreach_status",
    headerName: "External Outreach Status",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "ext_outreach_date",
    headerName: "External Outreach Date",
    minWidth: 180,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "admitted_date",
    headerName: "Admitted",
    minWidth: 100,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "discharged_date",
    headerName: "Discharge",
    minWidth: 180,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "facility_name",
    headerName: "Facility",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "facility_type",
    headerName: "Facility Type",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "dicharged_to",
    headerName: "Discharged To",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "days_since_discharge",
    headerName: "Days Since Discharge",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "next_appt_type",
    headerName: "Next Appt Type",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "next_appt_date",
    headerName: "Next Appt Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "next_appt_time",
    headerName: "Next Appt Time",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <div>{e.value ? dayjs(e.value).format("hh:mm a") : "N/A"}</div>
    ),
  },
];

const careProgramsColumns = (handleOnEditClick, handleAssignUser) => [
  {
    field: "edit",
    headerName: "",
    width: 100,
    align: "center",
    renderCell: (e) => (
      <>
        <IconButton onClick={() => handleOnEditClick(e.row)} color="secondary">
          <EditIcon fontSize="small" />
        </IconButton>
      </>
    ),
  },
  {
    field: "patient_id",
    headerName: "ID",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "care_oppt_category",
    headerName: "Care Opportunity Category",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "ext_source_name",
    headerName: "External Source Name",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "outreach_status",
    headerName: "Outreach Status",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "created_date",
    headerName: "Created Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "assigned_user_name",
    headerName: "Assigned User",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <>
        {e.value ? (
          <Typography>{e.value}</Typography>
        ) : (
          <Button
            onClick={() => handleAssignUser(e.row)}
            variant="text"
            color="secondary"
          >
            Assign To me
          </Button>
        )}
      </>
    ),
  },
  {
    field: "patient_name",
    headerName: "Name",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "patient_dob",
    headerName: "DOB",
    minWidth: 100,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "patient_age",
    headerName: "Age",
    minWidth: 80,
    flex: 1,
  },
  {
    field: "patient_gender",
    headerName: "Sex",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "patient_phone",
    headerName: "Phone",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "pcp_name",
    headerName: "PCP Name",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "care_program_name",
    headerName: "Care Program",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "care_program_status",
    headerName: "Care Program Status",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "auto_referral",
    headerName: "Auto Referral",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "next_appt_type",
    headerName: "Next Appt Type",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "next_appt_date",
    headerName: "Next Appt Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "next_appt_time",
    headerName: "Next Appt Time",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <div>{e.value ? dayjs(e.value).format("hh:mm a") : "N/A"}</div>
    ),
  },
];

const careGapColumns = (handleOnEditClick, handleAssignUser) => [
  {
    field: "edit",
    headerName: "",
    width: 100,
    align: "center",
    renderCell: (e) => (
      <>
        <IconButton onClick={() => handleOnEditClick(e.row)} color="secondary">
          <EditIcon fontSize="small" />
        </IconButton>
      </>
    ),
  },
  {
    field: "patient_id",
    headerName: "ID",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "care_oppt_category",
    headerName: "Care Opportunity Category",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "ext_source_name",
    headerName: "External Source Name",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "outreach_status",
    headerName: "Outreach Status",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "created_date",
    headerName: "Created Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "assigned_user_name",
    headerName: "Assigned User",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <>
        {e.value ? (
          <Typography>{e.value}</Typography>
        ) : (
          <Button
            onClick={() => handleAssignUser(e.row)}
            variant="text"
            color="secondary"
          >
            Assign To me
          </Button>
        )}
      </>
    ),
  },
  {
    field: "patient_name",
    headerName: "Name",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "patient_dob",
    headerName: "DOB",
    minWidth: 100,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "patient_phone",
    headerName: "Phone",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "payer_name",
    headerName: "Payer Name",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "provider_name",
    headerName: "Provider",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "care_gap_contract_type",
    headerName: "Contract Type",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "care_gap_name",
    headerName: "Care Gap",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "care_gap_status",
    headerName: "Care Gap Status",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "action_end_date",
    headerName: "Action End Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "performace_year",
    headerName: "Performance Year",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "next_appt_type",
    headerName: "Next Appt Type",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "next_appt_date",
    headerName: "Next Appt Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "next_appt_time",
    headerName: "Next Appt Time",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <div>{e.value ? dayjs(e.value).format("hh:mm a") : "N/A"}</div>
    ),
  },
];

const depressionColumns = (handleOnEditClick, handleAssignUser) => [
  {
    field: "edit",
    headerName: "",
    width: 100,
    align: "center",
    renderCell: (e) => (
      <>
        <IconButton onClick={() => handleOnEditClick(e.row)} color="secondary">
          <EditIcon fontSize="small" />
        </IconButton>
      </>
    ),
  },
  {
    field: "patient_id",
    headerName: "ID",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "care_oppt_category",
    headerName: "Care Opportunity Category",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "ext_source_name",
    headerName: "External Source Name",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "outreach_status",
    headerName: "Outreach Status",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "created_date",
    headerName: "Created Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "assigned_user_name",
    headerName: "Assigned User",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <>
        {e.value ? (
          <Typography>{e.value}</Typography>
        ) : (
          <Button
            onClick={() => handleAssignUser(e.row)}
            variant="text"
            color="secondary"
          >
            Assign To me
          </Button>
        )}
      </>
    ),
  },
  {
    field: "patient_name",
    headerName: "Name",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "patient_dob",
    headerName: "DOB",
    minWidth: 100,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "patient_phone",
    headerName: "Phone",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "patient_gender",
    headerName: "Gender",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "next_appt_type",
    headerName: "Next Appt Type",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "next_appt_date",
    headerName: "Next Appt Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "next_appt_time",
    headerName: "Next Appt Time",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <div>{e.value ? dayjs(e.value).format("hh:mm a") : "N/A"}</div>
    ),
  },
];

const emergencyColumns = (handleOnEditClick, handleAssignUser) => [
  {
    field: "edit",
    headerName: "",
    width: 100,
    align: "center",
    renderCell: (e) => (
      <>
        <IconButton onClick={() => handleOnEditClick(e.row)} color="secondary">
          <EditIcon fontSize="small" />
        </IconButton>
      </>
    ),
  },
  {
    field: "patient_id",
    headerName: "ID",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "care_oppt_category",
    headerName: "Care Opportunity Category",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "ext_source_name",
    headerName: "External Source Name",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    flex: 1,
  },
  {
    field: "outreach_status",
    headerName: "Outreach Status",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "created_date",
    headerName: "Created Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "assigned_user_name",
    headerName: "Assigned User",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <>
        {e.value ? (
          <Typography>{e.value}</Typography>
        ) : (
          <Button
            onClick={() => handleAssignUser(e.row)}
            variant="text"
            color="secondary"
          >
            Assign To me
          </Button>
        )}
      </>
    ),
  },
  {
    field: "patient_name",
    headerName: "Name",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "patient_dob",
    headerName: "DOB",
    minWidth: 100,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "patient_phone",
    headerName: "Phone",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "location",
    headerName: "Location",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "ext_outreach_status",
    headerName: "External Outreach Status",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "ext_outreach_date",
    headerName: "External Outreach Date",
    minWidth: 180,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "discharged_date",
    headerName: "Discharge Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    valueFormatter: valueFormatter,
  },
  {
    field: "facility_name",
    headerName: "Facility",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "next_appt_type",
    headerName: "Next Appt Type",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "next_appt_date",
    headerName: "Next Appt Date",
    minWidth: 150,
    flex: 1,
    valueGetter: valueGetter,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "next_appt_time",
    headerName: "Next Appt Time",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => (
      <div>{e.value ? dayjs(e.value).format("hh:mm a") : "N/A"}</div>
    ),
  },
];

export {
  hypertensionColumns,
  wellnessColumns,
  transitionOfCareColumns,
  careProgramsColumns,
  careGapColumns,
  depressionColumns,
  emergencyColumns,
};
